import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createClient,
  getClientsByEmail,
  getInvoice,
  getPayment,
  validateIban,
} from "../../../data";

import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import PDFViewerCustom from "@/components/pdf-viewer";
import FormSubmissionSummary from "@/components/form-submission-summary";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ClientViewDialog,
  InvoiceDetailItem,
  DateValidation,
} from "@/components";
import {
  approveInvoice,
  deleteCommissionDetail,
} from "@/data/requests/invoice-requests";
import { toast } from "@/components/ui/use-toast";

import { useEffect, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import ReactQuill from "react-quill";
import AuthStore from "@/stores/AuthStore";
import { PaymentDateModal } from "@/components/payment-date-modal";
import { ReturnModal } from "@/components/return-modal";
import {
  AlertCircle,
  Search,
  UserPlus,
  Scan,
  RefreshCw,
  Trash2,
  Send,
  Zap,
  Eye,
  Edit,
  Users,
  CheckCircle,
  AlertTriangle,
  Calculator,
  FileText,
  DollarSign,
  CheckCircle2,
  Download,
  RotateCcw,
} from "lucide-react";

export function InvoiceDetailScreen() {
  const [clientsWithSameEmail, setClientsWithSameEmail] = useState([]);
  const [filteredClients, setFilteredClients] = useState(null);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const [isClientViewOpen, setIsClientViewOpen] = useState(false);
  const [isPaymentDateModalOpen, setIsPaymentDateModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [ibanValidationResult, setIbanValidationResult] = useState(null);
  const [isValidatingIban, setIsValidatingIban] = useState(false);
  const [ibanDebounceTimeout, setIbanDebounceTimeout] = useState(null);
  const [dateValidationResult, setDateValidationResult] = useState(null);

  const { selectedGpt, selectedGptId } = useState("");

  const formButtonRef = useRef(null);
  const { id, invoiceId } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoiceDetail", invoiceId],
    queryFn: async () => await getInvoice(id, invoiceId),
    placeholderData: (previousData) => previousData,
  });

  // Cleanup: component unmount olduğunda timeout'u temizle
  useEffect(() => {
    return () => {
      if (ibanDebounceTimeout) {
        clearTimeout(ibanDebounceTimeout);
      }
    };
  }, [ibanDebounceTimeout]);

  // Sayfa yüklendiğinde otomatik IBAN doğrulama
  useEffect(() => {
    const clientIban = data?.data?.commissionDetail?.commission?.client?.IBAN;
    if (clientIban) {
      const validateClientIban = async () => {
        try {
          setIsValidatingIban(true);

          const result = await validateIban(clientIban);

          setIbanValidationResult(result);

          if (result.success && result.data.isValid) {
            toast({
              title: "IBAN Doğrulama Başarılı ✅",
              description: `IBAN geçerli. Ülke: ${result.data.countryName} (${result.data.countryCode})`,
              variant: "default",
            });
          } else {
            // Hata mesajını API response'undan al
            const errorMessage =
              result.data?.error || result.message || "IBAN geçersiz.";
            toast({
              title: "IBAN Doğrulama Başarısız ❌",
              description: errorMessage,
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("IBAN validation error:", error);

          // API hatasından detaylı mesaj al
          let errorMessage = "IBAN doğrulama sırasında bir hata oluştu.";

          if (error.response) {
            // API'den gelen hata response'u
            const apiError = error.response.data;
            if (apiError?.data?.error) {
              errorMessage = apiError.data.error;
            } else if (apiError?.message) {
              errorMessage = apiError.message;
            } else if (apiError?.error) {
              errorMessage = apiError.error;
            } else {
              errorMessage = `API Hatası: ${error.response.status} - ${error.response.statusText}`;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          toast({
            title: "IBAN Doğrulama Hatası",
            description: errorMessage,
            variant: "destructive",
          });
          setIbanValidationResult({ success: false, error: errorMessage });
        } finally {
          setIsValidatingIban(false);
        }
      };

      validateClientIban();
    }
  }, [data?.data?.commissionDetail?.commission?.client?.IBAN]);

  // Data yüklendiğinde form field'larını initialize et
  useEffect(() => {
    if (data?.data?.commissionDetail) {
      const commissionDetail = data.data.commissionDetail;
      // Tüm form field'larını set et
      setValue("invoiceDate", commissionDetail.invoiceDate);
      setValue("totalAmount", commissionDetail.totalAmount);
      setValue("iban", commissionDetail.iban);
      setValue("bic", commissionDetail.bic);
      setValue("bankAccountHolder", commissionDetail.bankAccountHolder);
      setValue("invoiceId", commissionDetail.invoiceId);
      setValue("ourCompany", commissionDetail.ourCompany);
    }
  }, [data, setValue]);

  const mutation = useMutation({
    mutationKey: ["approveInvoice"],
    mutationFn: (data) => {
      return approveInvoice(data);
    },
    onSuccess: async (response) => {
      queryClient.invalidateQueries(["invoices", id]);
      queryClient.invalidateQueries(["invoiceDetail", invoiceId]);

      if (response.data.message == "Mail Sent") {
        toast({
          title: "Mail Gönderildi ",
          message: "Fatura başarıyla onaylandı.",
        });
        navigate(`/payments/${id}/invoices`);
      } else if (response.data.status === "Accounting") {
        toast({
          title: "Accounting'e Gönderildi",
          description: "Fatura başarıyla Accounting statüsüne geçirildi.",
        });
      } else if (response.data.status === "LUCA") {
        toast({
          title: "LUCA'ya Gönderildi",
          description: "Fatura başarıyla LUCA statüsüne geçirildi.",
        });
      } else if (response.data.status === "Paid") {
        toast({
          title: "Ödendi",
          description: "Fatura başarıyla ödendi olarak işaretlendi.",
        });
      } else if (response.data.status === "Approved") {
        toast({
          title: "Fatura Onaylandı",
          description: "Fatura başarıyla onaylandı.",
        });
      } else {
        toast({
          title: "Fatura Güncellendi",
          description: "Fatura başarıyla güncellendi.",
        });
      }
    },
    onError: (error) => {
      console.error("Mutation error:", error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || error.message || "Bir hata oluştu",
      });
    },
  });
  const { mutate: deleteCommissionDetailMutation } = useMutation({
    mutationFn: async () =>
      await deleteCommissionDetail(data.data.commissionDetail.id),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Fatura detayı başarıyla silindi",
      });
      navigate(-1);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const createClientMutation = useMutation({
    mutationKey: ["createClient"],
    mutationFn: (data) => {
      return createClient(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(["invoiceDetail", invoiceId]);
      toast({
        title: "Müşteri Oluşturuldu",
        message: "Müşteri başarıyla oluşturuldu.",
      });
      onAccept("manuelClientSearch");
    },
    onError: (error) => {
      toast({
        title: error.response.data.message,
        type: "error",
      });
    },
  });
  const handleCreateClient = async () => {
    const bankName = await getBankName(
      data.data.commissionDetail.commission.country,
      data.data.commissionDetail.bic,
      data.data.commissionDetail.iban,
      data.data.commissionDetail.bankName
    );

    const clientData = {
      name: data.data.commissionDetail.commission.influencerName,
      email: data.data.commissionDetail.commission.email,
      IBAN: data.data.commissionDetail.iban,
      SWIFT: data.data.commissionDetail.bic,
      billingName: data.data.commissionDetail.bankAccountHolder,
      bankName: bankName,
      country: data.data.commissionDetail.commission.country,
      phoneNumber: data.data.commissionDetail.phoneNumber,
      isPrepareTheInvoiceIndividually: true, // Bireysel fatura kesiliyor (default)
    };
    createClientMutation.mutate(clientData);
  };
  const getBankName = async (country, swift, iban, bankName) => {
    if (country === "Azerbaycan") {
      return "Turan";
    }
    return bankName;

    
  };

  const getIsHide = (buttonName) => {
    if (isLoading) return true;

    const currentStatus = data.data.commissionDetail.status;

     if (currentStatus == buttonName) {
      return true;
    }
 
    switch (currentStatus) {
      case "Return":
        if (buttonName === "InBank") return false;
        return true;
      case "InBank":
        if (buttonName === "Paid") return false;        
        return true;
      case "Paid":
        if (buttonName === "Return") return false;
        return true;
      case "Invoice Uploaded":
        if (buttonName === "Approved") return false;
        if (buttonName === "Troubled") return false;
        return true;
      case "Auto Created Invoice":
        if (buttonName === "Approved") return false;
        if (buttonName === "Troubled") return false;
        return true;
      case "OCR Completed":
        if (buttonName === "Approved") return false;
        if (buttonName === "Troubled") return false;
        return true;
      case "Approved":
        if (buttonName === "Accounting") return false;
        if (buttonName === "Troubled") return false;
        return true;
      case "LUCA":
        if (buttonName === "InBank") return false;
        if (buttonName === "Paid") return false;
        return true; 
      case "WAITING":
        if (buttonName === "InBank") return false;
        return true;    
      default:
        return true;

    }
 
  };

  // Status'a göre badge rengi döndüren fonksiyon
  const getStatusBadgeClass = (status) => {
    const statusColors = {
      "Approved": "bg-blue-500 text-white",
      "Accounting": "bg-purple-500 text-white",
      "LUCA": "bg-red-500 text-white",
      "LUCAREADY": "bg-orange-500 text-white",
      "Paid": "bg-green-500 text-white",
      "Troubled": "bg-yellow-500 text-black",
      "Change Requested": "bg-amber-500 text-white",
      "Invoice Uploaded": "bg-indigo-500 text-white",
      "Mail Sended": "bg-cyan-500 text-white",
      "OCR Completed": "bg-teal-500 text-white",
      "Auto Created Invoice": "bg-violet-500 text-white",
      "InBank": "bg-emerald-500 text-white",
      "Not Paid": "bg-gray-500 text-white",
      "Return": "bg-rose-500 text-white",
    };
    return statusColors[status] || "bg-gray-400 text-white";
  };

  const pageSettings = {
    title: "Fatura Detayı",
    actions: [
      {
        name: "Listeye Dön",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate(`/payments/${id}/invoices`);
        },
      },

      {
        name: "PDF İndir",
        color: "bg-pink-500 text-white hover:bg-pink-600",
        onClick: () => {
          var link =
            "https://fantaslinkworld.blob.core.windows.net/invoicememberuploads/" +
            data.data.commissionDetail.invoicePath.replace(
              "public/uploads/paymentPdfs/",
              ""
            );

          window.open(link, "_blank");
        },
      },

      {
        name: "Kaydet",
        color: "bg-gray-500 text-white",
        icon: "add-icon",
        onClick: () => {
          if (!isCanEdit) {
            toast({
              variant: "destructive",
              title: "Bu faturayı düzenleyemezsiniz",
              description: "Bu fatura zaten ödenmiş",
              status: "error",
            });
            return;
          }

          formButtonRef.current.click();
        },
      },
    ],
  };

  const isCanEdit = data?.data?.commissionDetail?.status != "Paid";

  useEffect(() => {
    if (data?.data?.commissionDetail?.commission?.email) {
      getClientsByEmail(data.data.commissionDetail.commission.email).then(
        (res) => {
          setClientsWithSameEmail(res);
        }
      );
    }
  }, [data]);

  const controlAllFieldsApproved = () => {
    return (
      data?.data?.commissionDetail?.isOurCompanyApproved &&
      data?.data?.commissionDetail?.isTotalAmountApproved &&
      data?.data?.commissionDetail?.isIbanApproved &&
      data?.data?.commissionDetail?.isBicApproved &&
      data?.data?.commissionDetail?.isInvoiceDateApproved &&
      data?.data?.commissionDetail?.isBankAccountHolderApproved &&
      data?.data?.commissionDetail?.isInvoiceIdApproved
    );
  };

  const onSubmit = async (data) => {
    console.log("data form", data);
    mutation.mutate({ id: invoiceId, data: data });
  };

  const sendChangeRequest = async () => {
    if (!isCanEdit) {
      toast({
        variant: "destructive",
        title: "Bu faturayı düzenleyemezsiniz",
        description: "Bu fatura zaten ödenmiş",
        status: "error",
      });
      return;
    }
    console.log("sendChangeRequest");
    mutation.mutate({
      id: invoiceId,
      data: {
        sendMailAction: true,
        mailBodyMessage: getValues("mailBodyMessage"),
      },
    });
  };
  const handleClientChange = async (clientId) => {
    try {
      await mutation.mutateAsync({
        id: invoiceId,
        data: {
          clientId: parseInt(clientId),
        },
      });
      queryClient.invalidateQueries(["invoiceDetail", invoiceId]);
    } catch (error) {
      console.error("Error changing client:", error);
      toast({
        title: "Müşteri değiştirilirken hata oluştu",
        description: error.message,
        status: "error",
      });
    }
  };

  const onAccept = (field) => {
    if (!isCanEdit) {
      toast({
        variant: "destructive",
        title: "Bu faturayı düzenleyemezsiniz",
        description: "Bu fatura zaten ödenmiş",
        status: "error",
      });
      return;
    }

    switch (field) {
      case "isInvoiceIdApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isInvoiceIdApproved: true,
          },
        });
        break;
      case "isEmailApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isEmailApproved: true,
          },
        });
        break;
      case "isTotalAmountApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isTotalAmountApproved: true,
          },
        });
        break;
      case "isIbanApproved":
        // IBAN doğrulama kontrolü (Azerbaycan, Global veya IBAN_BYPASS izni hariç)
        const isAzerbaijan =
          data?.data?.commissionDetail?.commission?.country?.toLowerCase() ===
            "azerbaycan" ||
          data?.data?.commissionDetail?.commission?.country?.toLowerCase() ===
            "global";
        
        const hasIbanBypass = AuthStore.isHavePermission("IBAN_BYPASS");

        if (
          !isAzerbaijan &&
          !hasIbanBypass &&
          (!ibanValidationResult ||
            !ibanValidationResult.success ||
            !ibanValidationResult.data?.isValid)
        ) {
          toast({
            title: "IBAN Onaylanamadı ❌",
            description:
              "IBAN doğrulama geçmediği için approve edilemez. Önce geçerli bir IBAN girin.",
            variant: "destructive",
          });
          return;
        }

        mutation.mutate({
          id: invoiceId,
          data: {
            isIbanApproved: true,
          },
        });
        break;
      case "isBicApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isBicApproved: true,
          },
        });
        break;
      case "isInvoiceDateApproved":
        // Tarih doğrulama kontrolü
        if (
          !dateValidationResult ||
          !dateValidationResult.success ||
          !dateValidationResult.data?.isValid
        ) {
          toast({
            title: "Tarih Onaylanamadı ❌",
            description:
              "Tarih doğrulama geçmediği için approve edilemez. Önce geçerli bir tarih girin.",
            variant: "destructive",
          });
          return;
        }

        mutation.mutate({
          id: invoiceId,
          data: {
            isInvoiceDateApproved: true,
          },
        });
        break;
      case "isBankAccountHolderApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isBankAccountHolderApproved: true,
          },
        });
        break;
      case "isOurCompanyApproved":
        mutation.mutate({
          id: invoiceId,
          data: {
            isOurCompanyApproved: true,
          },
        });
        break;
      case "manuelClientSearch":
        mutation.mutate({
          id: invoiceId,
          data: {
            manuelClientSearch: true,
          },
        });
        break;
    }
  };

  const handleUpdateStatus = async (status) => {
    if (status === "Paid") {
      setSelectedInvoice(id);
      setIsPaymentDateModalOpen(true);
      return;
    }

    try {
      await mutation.mutateAsync({
        id,
        data: { status },
      });
      queryClient.invalidateQueries(["invoice", id]);
      toast({
        title: "Başarılı",
        description: "Fatura durumu başarıyla güncellendi",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Fatura durumu güncellenemedi",
        variant: "destructive",
      });
    }
  };

  const handlePaymentDateConfirm = async (paymentDate) => {
    try {
      await mutation.mutateAsync({
        id: selectedInvoice,
        data: {
          status: "Paid",
          paymentDate: paymentDate,
        },
      });
      queryClient.invalidateQueries(["invoice", selectedInvoice]);
      toast({
        title: "Başarılı",
        description: "Fatura ödendi olarak işaretlendi",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Fatura durumu güncellenemedi",
        variant: "destructive",
      });
    } finally {
      setIsPaymentDateModalOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleReturnConfirm = async (returnDate, returnReason) => {
    try {
      await mutation.mutateAsync({
        id: selectedInvoice,
        data: {
          status: "Return",
          returnDate: returnDate,
          returnReason: returnReason,
        },
      });
      queryClient.invalidateQueries(["invoice", selectedInvoice]);
      toast({
        title: "Başarılı",
        description: "Fatura iade olarak işaretlendi",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Fatura durumu güncellenemedi",
        variant: "destructive",
      });
    } finally {
      setIsReturnModalOpen(false);
      setSelectedInvoice(null);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div>Yükleniyor...</div>
      </AppLayout>
    );
  }

  const getLucaIdMatch = () => {
    console.log(
      data?.data?.commissionDetail?.commission?.client?.lucaAccountName?.toLocaleUpperCase(
        "tr-TR"
      ),
      data?.data?.commissionDetail?.bankAccountHolder?.toLocaleUpperCase()
    );
    return (
      data?.data?.commissionDetail?.commission?.client?.lucaAccountName?.toLocaleUpperCase() ==
        data?.data?.commissionDetail?.bankAccountHolder?.toLocaleUpperCase() ||
      data?.data?.commissionDetail?.commission?.client?.lucaAccountName?.toLocaleLowerCase() ==
        data?.data?.commissionDetail?.bankAccountHolder?.toLocaleLowerCase() ||
      data?.data?.commissionDetail?.commission?.client?.lucaAccountName?.toLocaleUpperCase(
        "tr-TR"
      ) ==
        data?.data?.commissionDetail?.bankAccountHolder?.toLocaleUpperCase(
          "tr-TR"
        ) ||
      data?.data?.commissionDetail?.commission?.client?.lucaAccountName?.toLocaleLowerCase(
        "tr-TR"
      ) ==
        data?.data?.commissionDetail?.bankAccountHolder?.toLocaleLowerCase(
          "tr-TR"
        )
    );
  };

  const handleDeleteCommissionDetail = () => {
    deleteCommissionDetailMutation(data.data.commissionDetail.id);
  };

  const validateIbanFunction = async (ibanToValidate = null) => {
    try {
      setIsValidatingIban(true);

      // IBAN'ı belirle - parametre olarak gelirse onu kullan, yoksa form state'indeki değeri kullan
      const iban =
        ibanToValidate ||
        watch("iban") ||
        data?.data?.commissionDetail?.commission?.client?.IBAN ||
        data?.data?.commissionDetail?.iban;

      if (!iban) {
        toast({
          title: "IBAN Bulunamadı",
          description: "Doğrulanacak IBAN bulunamadı.",
          variant: "destructive",
        });
        return;
      }

      const result = await validateIban(iban);

      setIbanValidationResult(result);

      if (result.success && result.data.isValid) {
        toast({
          title: "IBAN Doğrulama Başarılı ✅",
          description: `IBAN geçerli. Ülke: ${result.data.countryName} (${result.data.countryCode})`,
          variant: "default",
        });
      } else {
        // Hata mesajını API response'undan al
        const errorMessage =
          result.data?.error || result.message || "IBAN geçersiz.";
        toast({
          title: "IBAN Doğrulama Başarısız ❌",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("IBAN validation error:", error);

      // API hatasından detaylı mesaj al
      let errorMessage = "IBAN doğrulama sırasında bir hata oluştu.";

      if (error.response) {
        // API'den gelen hata response'u
        const apiError = error.response.data;
        if (apiError?.data?.error) {
          errorMessage = apiError.data.error;
        } else if (apiError?.message) {
          errorMessage = apiError.message;
        } else if (apiError?.error) {
          errorMessage = apiError.error;
        } else {
          errorMessage = `API Hatası: ${error.response.status} - ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "IBAN Doğrulama Hatası",
        description: errorMessage,
        variant: "destructive",
      });
      setIbanValidationResult({ success: false, error: errorMessage });
    } finally {
      setIsValidatingIban(false);
    }
  };

  const normalizeText = (text) => {
    return text
      ?.toLocaleLowerCase("tr-TR")
      .replace(/[İIıi]/g, "i")
      .replace(/[ğĞ]/g, "g")
      .replace(/[üÜ]/g, "u")
      .replace(/[şŞ]/g, "s")
      .replace(/[çÇ]/g, "c")
      .replace(/[öÖ]/g, "o");
  };

  return (
    <AppLayout
      fullscreen={true}
      title={
        <div className="flex items-center gap-3">
          <span>Invoice Detail - {data?.data?.commissionDetail?.id}</span>
          <Badge className={`${getStatusBadgeClass(data?.data?.commissionDetail?.status)} px-3 py-1 text-sm font-medium`}>
            {data?.data?.commissionDetail?.status}
          </Badge>
        </div>
      }
      actions={pageSettings.actions}
      loading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-6 space-y-6">
        {/* Status & Actions Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Client Status Card - Dynamic */}
          {!data.data.commissionDetail.commission.client ? (
            // No Client Found
            <Card className="border-l-4 border-l-orange-500 bg-orange-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertCircle className="h-5 w-5" />
                  Müşteri Bulunamadı
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-orange-600">
                  Lütfen müşteriyi elle arayın veya otomatik olarak yeni müşteri oluşturun.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-orange-300 hover:bg-orange-100"
                    onClick={() => {
                      onAccept("manuelClientSearch");
                    }}
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Müşteri Bul ve Bağla
                  </Button>
                  <Button
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleCreateClient}
                    disabled={createClientMutation.isLoading}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    {createClientMutation.isLoading ? "Oluşturuluyor..." : "Create New Client"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : !getLucaIdMatch() ? (
            // Client Found but Luca Mismatch
            <Card className="border-l-4 border-l-red-500 bg-red-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  Luca Hesap Adı Uyuşmuyor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-red-600">
                  Luca hesap adı eşleşmiyor. Lütfen güncelleyin veya farklı bir müşteri seçin.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Dialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-red-300 hover:bg-red-100">
                        <Users className="mr-2 h-4 w-4" />
                        {data.data.commissionDetail.commission.client.name} (
                        {data.data.commissionDetail.commission.client.lucaAccountName})
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px]">
                      <DialogHeader>
                        <DialogTitle>Müşteri Seç</DialogTitle>
                        <DialogDescription>
                          Luca Hesap Adına göre müşteri arayıp seçin
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col gap-4 py-4">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Luca Hesap Adı veya Müşteri Adı ile ara..."
                            onChange={(e) => {
                              const searchValue = normalizeText(e.target.value);
                              const filteredClients = clientsWithSameEmail.filter(
                                (client) =>
                                  normalizeText(client.lucaAccountName)?.includes(searchValue) ||
                                  normalizeText(client.name)?.includes(searchValue)
                              );
                              setFilteredClients(filteredClients);
                            }}
                            className="pl-9"
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto border rounded-md divide-y">
                          {(filteredClients || clientsWithSameEmail).length === 0 ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              Müşteri bulunamadı.
                            </div>
                          ) : (
                            (filteredClients || clientsWithSameEmail).map((client) => (
                              <div
                                key={client.id}
                                className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer transition-colors"
                                onClick={() => {
                                  handleClientChange(client.id.toString());
                                  setIsClientDialogOpen(false);
                                }}
                              >
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                                  <Users className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-foreground truncate">
                                    {client.name}
                                  </div>
                                  <div className="text-sm text-muted-foreground truncate">
                                    Luca: {client.lucaAccountName}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Seç
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="outline"
                    className="border-red-300 hover:bg-red-100"
                    onClick={async () => {
                      try {
                        await handleCreateClient();
                      } catch (error) {
                        toast({
                          title: "Hata",
                          description: "Müşteri oluşturulamadı: " + error.message,
                          variant: "destructive",
                        });
                      }
                    }}
                    disabled={createClientMutation.isLoading}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    {createClientMutation.isLoading ? "Oluşturuluyor..." : "Bağlı Müşteri Oluştur"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Client Found and Luca Match
            <Card className="border-l-4 border-l-green-500 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  Luca ID Eşleşti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-green-600">
                  Müşteri ve Luca hesabı doğru şekilde eşleşmiş.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions Card */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              {/* Row 1: General Actions */}
              <div className="flex flex-wrap gap-2 mb-4">
                {/* OCR Dialog */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="secondary" size="sm">
                      <Scan className="mr-2 h-4 w-4" />
                      OCR Çalıştır
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>OCR Çalıştır</DialogTitle>
                      <DialogDescription>
                        Fatura verilerini çıkarmak için bir GPT prompt'u seçip OCR çalıştırın.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid items-center gap-2">
                        <Label htmlFor="gptPrompt">GPT Prompt</Label>
                        <Select value={selectedGpt} onValueChange={selectedGptId}>
                          <SelectTrigger>
                            <SelectValue placeholder="GPT Prompt Seç" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>GPT Prompt'ları</SelectLabel>
                              {data.data.gptPrompts.map((prompt) => (
                                <SelectItem value={prompt.id.toString()} key={prompt.id}>
                                  {prompt.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        onClick={() => {
                          if (!isCanEdit) {
                            toast({
                              variant: "destructive",
                              title: "Bu faturayı düzenleyemezsiniz",
                              description: "Bu fatura zaten ödenmiş",
                              status: "error",
                            });
                            return;
                          }
                          mutation.mutate({
                            id: invoiceId,
                            data: {
                              gptPrompt: selectedGptId,
                            },
                          });
                        }}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        OCR Çalıştır
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* View Client Card */}
                {data.data.commissionDetail.commission.client && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsClientViewOpen(true)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Müşteriyi Gör
                  </Button>
                )}

                {/* Edit Client */}
                {data.data.commissionDetail.commission.client && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/clients/${data.data.commissionDetail.commission.client.id}`
                      )
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Müşteriyi Düzenle
                  </Button>
                )}

                {/* Delete Button */}
                {AuthStore.isHavePermission("PAYMENT_DELETE") &&
                  data.data.commissionDetail.status !== "LUCA" &&
                  data.data.commissionDetail.status !== "Paid" &&
                  data.data.commissionDetail.status !== "InBank" &&
                  data.data.commissionDetail.status !== "WAITING" &&
                  data.data.commissionDetail.status !== "Accounting" &&
                  data.data.commissionDetail.status !== "LUCAREADY" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Sil
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Bundan kesinlikle emin misiniz?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Bu işlem geri alınamaz. Fatura kalıcı olarak silinecek.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteCommissionDetail}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Sil
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}

                {/* Change Request Dialog - Accounting sonrası statülerde gösterilmez */}
                {!["LUCA", "LUCAREADY","InBank","WAITING","RETURN" ,"Paid", "Accounting"].includes(data?.data?.commissionDetail?.status) && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Send className="mr-2 h-4 w-4" />
                      Değişiklik Talebi
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                      <DialogTitle>Değişiklik Talebi Gönder</DialogTitle>
                      <DialogDescription>
                        Değişiklik talebi hakkında müşteriyi bilgilendirmek için bir mesaj yazın.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid items-center">
                        <Label htmlFor="mailBodyMessage">Mesaj</Label>
                        <ReactQuill
                          theme="snow"
                          onChange={(value) => setValue("mailBodyMessage", value)}
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" onClick={() => sendChangeRequest()}>
                        <Send className="mr-2 h-4 w-4" />
                        Mail Gönder
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                )}
              </div>

              {/* Separator Line */}
              <div className="w-full h-px bg-border my-3"></div>

              {/* Row 2: Status Transition Actions */}
              <div className="flex flex-wrap gap-2">
                {/* Mark As Troubled */}
                {!getIsHide("Troubled") && (
                <Button
                  type="button"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  size="sm"
                  onClick={() => {
                    if (!isCanEdit) {
                      toast({
                        variant: "destructive",
                        title: "Bu faturayı düzenleyemezsiniz",
                        description: "Bu fatura zaten ödenmiş",
                        status: "error",
                      });
                      return;
                    }

                    const troubleReason = prompt("Lütfen sorun sebebini girin:");
                    if (!troubleReason) {
                      toast({
                        variant: "destructive",
                        title: "Sorun sebebi zorunludur",
                        status: "error",
                      });
                      return;
                    }

                    mutation.mutate({
                      id: invoiceId,
                      data: {
                        status: "Troubled",
                        troubleReason: troubleReason,
                      },
                    });
                  }}
                >
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Sorunlu İşaretle
                </Button>
                )}

                {/* Accounting */}
                {!getIsHide("Accounting") && (
                  <Button
                    type="button"
                    className="bg-purple-700 hover:bg-purple-800 text-white"
                    size="sm"
                    onClick={() => {
                      console.log("Accounting button clicked");
                      console.log("clientId:", data.data.commissionDetail.commission.clientId);
                      console.log("client:", data.data.commissionDetail.commission.client);
                      console.log("status:", data.data.commissionDetail.status);
                      
                      // Client atanmamış mı kontrolü
                      const clientId = data.data.commissionDetail.commission.clientId;
                      if (!clientId) {
                
                        toast({
                          variant: "destructive",
                          title: "Accounting'e Gönderilemez",
                          description: "Bu faturaya henüz bir müşteri atanmamış. Önce müşteri ataması yapın.",
                        });
                        return;
                      }
                      
                      // Client bilgisi var mı kontrolü
                      const client = data.data.commissionDetail.commission.client;
                      if (!client) {
                        console.log("BLOCKED: No client object");
                        toast({
                          variant: "destructive",
                          title: "Accounting'e Gönderilemez",
                          description: "Müşteri bilgisi bulunamadı. Lütfen sayfayı yenileyin.",
                        });
                        return;
                      }
                                        
                      
                      // Status kontrolü
                      if (data.data.commissionDetail.status !== "Approved") {
                        console.log("BLOCKED: Status is not Approved");
                        toast({
                          variant: "destructive",
                          title: "Accounting'e Gönderilemez",
                          description: "Bu fatura henüz Approved statüsünde değil.",
                        });
                        return;
                      }
                      
                      // Paid kontrolü
                      if (!isCanEdit) {
                        console.log("BLOCKED: Cannot edit (already paid)");
                        toast({
                          variant: "destructive",
                          title: "Düzenlenemez",
                          description: "Bu fatura zaten ödenmiş.",
                        });
                        return;
                      }

                      console.log("All checks passed, calling mutation...");
                      mutation.mutate({
                        id: invoiceId,
                        data: {
                          status: "Accounting",
                          troubleReason: "",
                        },
                      });
                    }}
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    Muhasebe
                  </Button>
                )}

                {/* LUCA - Bu ekranda gösterilmiyor, fatura listesi ekranından işlem yapılmalı */}
                {false && !getIsHide("LUCA") && AuthStore.isHavePermission("MARK_LUCA") && (
                  <Button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white"
                    size="sm"
                    onClick={() => {
                      if (data.data.commissionDetail.status != "Accounting") {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura Muhasebe durumunda değil",
                          status: "error",
                        });
                        return;
                      }
                      if (!isCanEdit) {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura zaten ödenmiş",
                          status: "error",
                        });
                        return;
                      }

                      mutation.mutate({
                        id: invoiceId,
                        data: {
                          status: "LUCA",
                          troubleReason: "",
                        },
                      });
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    LUCA
                  </Button>
                )}

                {/* Paid - LUCA statüsünde Paid'e geçiş yapılabilir */}
                {!getIsHide("Paid") && (
                  <Button
                    type="button"
                    className="bg-green-500 hover:bg-green-600 text-white"
                    size="sm"
                    onClick={() => {
                      if (data.data.commissionDetail.status != "LUCA" && data.data.commissionDetail.status != "InBank") {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura LUCA durumunda değil",
                          status: "error",
                        });
                        return;
                      }

                      if (!isCanEdit) {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura zaten ödenmiş",
                          status: "error",
                        });
                        return;
                      }

                      setSelectedInvoice(invoiceId);
                      setIsPaymentDateModalOpen(true);
                    }}
                  >
                    <DollarSign className="mr-2 h-4 w-4" />
                    Ödendi
                  </Button>
                )}

                {/* Return - Sadece Paid durumunda göster */}
                {data?.data?.commissionDetail?.status === "Paid" && (
                  <Button
                    type="button"
                    className="bg-rose-500 hover:bg-rose-600 text-white"
                    size="sm"
                    onClick={() => {
                      setSelectedInvoice(invoiceId);
                      setIsReturnModalOpen(true);
                    }}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    İade
                  </Button>
                )}

                {/* InBank - Return durumunda göster */}
                {!getIsHide("InBank") && (
                  <Button
                    type="button"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    size="sm"
                    onClick={() => {
                      mutation.mutate({
                        id: invoiceId,
                        data: {
                          status: "InBank",
                        },
                      });
                    }}
                  >
                    <DollarSign className="mr-2 h-4 w-4" />
                    Bankada
                  </Button>
                )}

                {/* Approve */}
                {!getIsHide("Approved") && (
                  <Button
                    type="button"
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                    size="sm"
                    onClick={() => {
                      if (data.data.commissionDetail.status == "Approved") {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura zaten onaylanmış",
                          status: "error",
                        });
                        return;
                      }
                      if (!controlAllFieldsApproved()) {
                        toast({
                          variant: "destructive",
                          title: "Lütfen tüm işlemleri onaylayınız.",
                          description:
                            "Lütfen tüm işlemleri onaylayınız. Onaylanmamış alanlar var.",
                          status: "error",
                        });
                        return;
                      }

                      if (!isCanEdit) {
                        toast({
                          variant: "destructive",
                          title: "Bu faturayı düzenleyemezsiniz",
                          description: "Bu fatura zaten ödenmiş",
                          status: "error",
                        });
                        return;
                      }

                      mutation.mutate({
                        id: invoiceId,
                        data: {
                          status: "Approved",
                          troubleReason: "",
                        },
                      });
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Onayla
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Client View Dialog */}
        <ClientViewDialog
          isOpen={isClientViewOpen}
          onClose={() => setIsClientViewOpen(false)}
          clientId={data?.data?.commissionDetail?.commission?.client?.id}
        />

        {/* Alerts Section */}
        {(data.data.commissionDetail.troubleReason || data.data.commissionDetail.status === "Paid") && (
          <div className="space-y-4">
            {/* Trouble Reason Alert */}
            {data.data.commissionDetail.troubleReason && (
              <Card className="border-l-4 border-l-red-500 bg-red-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    Sorun Sebebi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-600">
                    {data.data.commissionDetail.troubleReason}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Paid Date Alert */}
            {data.data.commissionDetail.status === "Paid" && (
              <Card className="border-l-4 border-l-green-500 bg-green-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    Ödeme Tamamlandı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-green-600">
                    Ödeme tarihi: <strong>{data.data.commissionDetail.paymentDate}</strong>
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Invoice Form Fields */}
        <div>
          <div className="flex">
            <div className="flex flex-col w-1/3 gap-1 invoice-fields-container">
              <InvoiceDetailItem
                label="Şirketimiz"
                ourValue={"ENG İş Geliştirme ve Yatırım Ltd. Şti"}
                ocrValue={data.data.commissionDetail.ourCompany}
                value={watch("ourCompany")} // Controlled value
                isApproved={data.data.commissionDetail.isOurCompanyApproved}
                onAccept={() => {
                  onAccept("isOurCompanyApproved");
                }}
                onInputChanged={(value) => {
                  setValue("ourCompany", value);
                }}
                showCopyButton={true}
              />
              <InvoiceDetailItem
                label="Tutar"
                ourValue={data.data.commissionDetail.commission.finalAmount}
                ocrValue={data.data.commissionDetail.totalAmount}
                value={watch("totalAmount")} // Controlled value
                isApproved={data.data.commissionDetail.isTotalAmountApproved}
                onAccept={() => {
                  onAccept("isTotalAmountApproved");
                }}
                onInputChanged={(value) => {
                  setValue("totalAmount", value);
                }}
                showCopyButton={true}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",

                  borderRadius: "10px",
                  border: "1px solid rgb(1, 1, 1)",
                }}
              >
                <InvoiceDetailItem
                  label="IBAN"
                  ourValue={
                    data.data.commissionDetail.commission.client == null
                      ? "Müşteri Bulunamadı - Yeni Müşteri Oluştur"
                      : data.data.commissionDetail.commission.client.IBAN
                  }
                  ocrValue={data.data.commissionDetail.iban}
                  value={watch("iban")} // Controlled value
                  isApproved={data.data.commissionDetail.isIbanApproved}
                  onAccept={() => {
                    onAccept("isIbanApproved");
                  }}
                  // IBAN geçersizse accept butonunu disable et (Azerbaycan, Global veya IBAN_BYPASS izni hariç)
                  disableAccept={
                    data.data.commissionDetail.commission.country?.toLowerCase() ===
                      "azerbaycan" ||
                    data.data.commissionDetail.commission.country?.toLowerCase() ===
                      "global" ||
                    AuthStore.isHavePermission("IBAN_BYPASS")
                      ? false
                      : !ibanValidationResult ||
                        !ibanValidationResult.success ||
                        !ibanValidationResult.data?.isValid
                  }
                  onInputChanged={(value) => {
                    setValue("iban", value);

                    // Önceki timeout'u temizle
                    if (ibanDebounceTimeout) {
                      clearTimeout(ibanDebounceTimeout);
                    }

                    if (value && value.trim().length > 0) {
                      // 1 saniye bekle, sonra doğrulama yap (debounce)
                      const newTimeout = setTimeout(() => {
                        validateIbanFunction(value.trim());
                      }, 1000);
                      setIbanDebounceTimeout(newTimeout);
                    } else {
                      // Boş ise doğrulama sonucunu sıfırla
                      setIbanValidationResult(null);
                    }
                  }}
                  showCopyButton={true}
                />

                {/* IBAN Doğrulama Bölümü */}
                <div className="mt-2 p-3 bg-muted rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-foreground">
                        IBAN Doğrulama:
                      </h4>
                      {ibanValidationResult && (
                        <Badge
                          variant={
                            ibanValidationResult.success &&
                            ibanValidationResult.data?.isValid
                              ? "default"
                              : "destructive"
                          }
                        >
                          {ibanValidationResult.success &&
                          ibanValidationResult.data?.isValid
                            ? `✅ Geçerli (${ibanValidationResult.data.countryCode})`
                            : "❌ Geçersiz"}
                        </Badge>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => validateIbanFunction()}
                        disabled={isValidatingIban}
                      >
                        {isValidatingIban
                          ? "Kontrol Ediliyor..."
                          : ibanValidationResult
                          ? "🔄 Tekrar Kontrol Et"
                          : "IBAN Kontrol Et"}
                      </Button>
                    </div>
                  </div>

                  {ibanValidationResult && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {ibanValidationResult.success &&
                      ibanValidationResult.data?.isValid ? (
                        <div className="text-green-600">
                          <p>✅ IBAN: {ibanValidationResult.data.iban}</p>
                          <p>
                            🌍 Ülke: {ibanValidationResult.data.countryName}
                          </p>
                          <p>
                            📏 Uzunluk: {ibanValidationResult.data.length}{" "}
                            karakter
                          </p>
                        </div>
                      ) : (
                        <div className="text-red-600">
                          <p>
                            ❌{" "}
                            {ibanValidationResult.data?.error ||
                              ibanValidationResult.error}
                          </p>
                          {ibanValidationResult.data?.expectedLength && (
                            <p>
                              📏 Beklenen uzunluk:{" "}
                              {ibanValidationResult.data.expectedLength},
                              Mevcut: {ibanValidationResult.data.actualLength}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <InvoiceDetailItem
                label="SWIFT"
                ourValue={
                  data.data.commissionDetail.commission.client == null
                    ? "Müşteri Bulunamadı - Yeni Müşteri Oluştur"
                    : data.data.commissionDetail.commission.client.SWIFT
                }
                ocrValue={data.data.commissionDetail.bic}
                value={watch("bic")} // Controlled value
                isApproved={data.data.commissionDetail.isBicApproved}
                onAccept={() => {
                  onAccept("isBicApproved");
                }}
                onInputChanged={(value) => {
                  setValue("bic", value);
                }}
                showCopyButton={true}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",

                  borderRadius: "10px",
                  border: "1px solid rgb(1, 1, 1)",
                }}
              >
                <InvoiceDetailItem
                  label="Fatura Tarihi"
                  ourValue={data.data.commissionDetail.commission.invoiceId}
                  ocrValue={data.data.commissionDetail.invoiceDate}
                  value={watch("invoiceDate")} // Controlled value
                  isApproved={data.data.commissionDetail.isInvoiceDateApproved}
                  onAccept={() => {
                    onAccept("isInvoiceDateApproved");
                  }}
                  onInputChanged={(value) => {
                    setValue("invoiceDate", value);
                  }}
                  // Tarih geçersizse accept butonunu disable et
                  disableAccept={
                    !dateValidationResult ||
                    !dateValidationResult.success ||
                    !dateValidationResult.data?.isValid
                  }
                />

                {/* Tarih Doğrulama Bölümü */}
                <DateValidation
                  invoiceDate={
                    watch("invoiceDate") ||
                    data.data.commissionDetail.invoiceDate
                  }
                  minimumDate={
                    data.data.commissionDetail.commission.minAcceptedInvoiceDate
                      ? (() => {
                          const date = new Date(
                            data.data.commissionDetail.commission.minAcceptedInvoiceDate
                          );
                          // UTC kullan - database'de UTC olarak saklanıyor
                          const day = date
                            .getUTCDate()
                            .toString()
                            .padStart(2, "0");
                          const month = (date.getUTCMonth() + 1)
                            .toString()
                            .padStart(2, "0");
                          const year = date.getUTCFullYear();
                          return `${day}.${month}.${year}`;
                        })()
                      : "01.01.2020"
                  }
                  onValidationChange={setDateValidationResult}
                  onDateConverted={(convertedDate) => {
                    // Dönüştürülmüş tarihi form input'una set et
                    setValue("invoiceDate", convertedDate);
                    toast({
                      title: "Tarih Otomatik Güncellendi",
                      description: `Invoice Date alanı "${convertedDate}" olarak güncellendi.`,
                      variant: "default",
                    });
                  }}
                  disabled={!isCanEdit}
                />
              </div>
              <InvoiceDetailItem
                label="Banka Hesap Adı"
                ourValue={
                  data.data.commissionDetail.commission.client == null
                    ? "Müşteri Bulunamadı - Yeni Müşteri Oluştur"
                    : data.data.commissionDetail.commission.client.billingName
                }
                ocrValue={data.data.commissionDetail.bankAccountHolder}
                value={watch("bankAccountHolder")} // Controlled value
                isApproved={
                  data.data.commissionDetail.isBankAccountHolderApproved
                }
                onAccept={() => {
                  onAccept("isBankAccountHolderApproved");
                }}
                onInputChanged={(value) => {
                  setValue("bankAccountHolder", value);
                }}
                showCopyButton={true}
              />

              <div className="mb-6">
                <InvoiceDetailItem
                  label="Fatura No"
                  ourValue={""}
                  ocrValue={data.data.commissionDetail.invoiceId}
                  value={watch("invoiceId")} // Controlled value
                  isApproved={data.data.commissionDetail.isInvoiceIdApproved}
                  onAccept={() => {
                    onAccept("isInvoiceIdApproved");
                  }}
                  onInputChanged={(value) => {
                    setValue("invoiceId", value);
                  }}
                  hideMatch={true}
                  hideClient={true}
                />

                {data.data.commissionDetail.isHaveAllreadyInvoiceId && (
                  <div className="flex flex-row gap-2 bg-red-500 p-3 rounded-md mb-3 text-white">
                    <h1>Bu fatura numarası zaten kullanılmış</h1>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <label
                  className="block text-foreground text-sm font-bold mb-2"
                  htmlFor="currency"
                >
                  Para Birimi
                </label>

                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-foreground bg-background leading-tight focus:outline-none focus:shadow-outline"
                  id="currency"
                  type="text"
                  placeholder="currency"
                  // bind the input value to the client's role
                  defaultValue={data.data.commissionDetail.currency}
                />
              </div>
              <div className="mb-6">
                <label
                  className="block text-foreground text-sm font-bold mb-2"
                  htmlFor="dueDate"
                >
                  Vade Tarihi
                </label>

                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-foreground bg-background leading-tight focus:outline-none focus:shadow-outline"
                  id="dueDate"
                  type="text"
                  placeholder="dueDate"
                  // bind the input value to the client's role
                  defaultValue={data.data.commissionDetail.dueDate}
                />
              </div>

              <div className="mb-6">
                <label
                  className="block text-foreground text-sm font-bold mb-2"
                  htmlFor="description"
                >
                  Açıklama
                </label>

                <textarea
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-foreground bg-background leading-tight focus:outline-none focus:shadow-outline"
                  id="description"
                  placeholder="description"
                  // bind the input value to the client's role
                  defaultValue={data.data.commissionDetail.description}
                />
              </div>
            </div>

            <div className="w-2/3">
              {/* istisna / gider pusulası akışı: fatura yok, form verileri (+ varsa istisna belgesi) gösterilir */}
              {data.data.commissionDetail.submissionType === "FORM" ||
              data.data.commissionDetail.exemptionDocPath ? (
                <FormSubmissionSummary
                  commissionDetail={data.data.commissionDetail}
                  documentHeight="600px"
                />
              ) : !data.data.commissionDetail.invoicePath ? (
                <div className="flex h-[800px] items-center justify-center rounded-md border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                  Yüklenmiş bir fatura bulunmuyor.
                </div>
              ) : data.data.commissionDetail.invoicePath.includes(".pdf") ? (
                <PDFViewerCustom
                  commissionDetailId={data.data.commissionDetail.id}
                  height="800px"
                />
              ) : (
                <div className="flex flex-col gap-1">
                  <img
                    alt="Fatura"
                    src={
                      "https://fantaslinkworld.blob.core.windows.net/invoicememberuploads/" +
                      data.data.commissionDetail.invoicePath.replace(
                        "public/uploads/paymentPdfs/",
                        ""
                      )
                    }
                  />
                </div>
              )}
            </div>
          </div>
          <Button type="submit" ref={formButtonRef} className="hidden">
            Kaydet
          </Button>
        </div>
      </form>

      <PaymentDateModal
        isOpen={isPaymentDateModalOpen}
        onClose={() => {
          setIsPaymentDateModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handlePaymentDateConfirm}
        invoiceId={selectedInvoice}
      />

      <ReturnModal
        isOpen={isReturnModalOpen}
        onClose={() => {
          setIsReturnModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handleReturnConfirm}
        invoiceId={selectedInvoice}
      />
    </AppLayout>
  );
}
